import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react'

export type GroupTab = 'prayers' | 'discussions'

interface GroupNavigationContextType {
  groupId: string | null
  activeTab: GroupTab
  prayerBadgeCount: number
  discussionBadgeCount: number
  setActiveTab: (tab: GroupTab) => void
  enterGroup: (id: string) => void
  leaveGroup: () => void
  setPrayerBadgeCount: (count: number) => void
  setDiscussionBadgeCount: (count: number) => void
  adjustPrayerBadgeCount: (delta: number) => void
  adjustDiscussionBadgeCount: (delta: number) => void
}

const GroupNavigationContext = createContext<GroupNavigationContextType | undefined>(undefined)

export function GroupNavigationProvider({ children }: { children: ReactNode }) {
  const [groupId, setGroupId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<GroupTab>('prayers')
  const [prayerBadgeCount, setPrayerBadgeCount] = useState(0)
  const [discussionBadgeCount, setDiscussionBadgeCount] = useState(0)
  const groupIdRef = useRef<string | null>(null)

  const enterGroup = useCallback((id: string) => {
    if (id !== groupIdRef.current) {
      groupIdRef.current = id
      setGroupId(id)
      setActiveTab('prayers')
      setPrayerBadgeCount(0)
      setDiscussionBadgeCount(0)
    }
  }, [])

  const leaveGroup = useCallback(() => {
    groupIdRef.current = null
    setGroupId(null)
    setActiveTab('prayers')
    setPrayerBadgeCount(0)
    setDiscussionBadgeCount(0)
  }, [])

  const adjustPrayerBadgeCount = useCallback((delta: number) => {
    setPrayerBadgeCount(prev => Math.max(0, prev + delta))
  }, [])

  const adjustDiscussionBadgeCount = useCallback((delta: number) => {
    setDiscussionBadgeCount(prev => Math.max(0, prev + delta))
  }, [])

  return (
    <GroupNavigationContext.Provider value={{
      groupId, activeTab, prayerBadgeCount, discussionBadgeCount,
      setActiveTab, enterGroup, leaveGroup,
      setPrayerBadgeCount, setDiscussionBadgeCount,
      adjustPrayerBadgeCount, adjustDiscussionBadgeCount,
    }}>
      {children}
    </GroupNavigationContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useGroupNavigation() {
  const context = useContext(GroupNavigationContext)
  if (!context) {
    throw new Error('useGroupNavigation must be used within a GroupNavigationProvider')
  }
  return context
}
